import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { LanguageSelector } from "./LanguageSelector";
import { useLang } from "../i18n/LanguageContext";

export const Header = () => {
  const { t, path } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const links = [
    { to: "/odf-technology", key: "odf-technology", label: t.nav.technology },
    { to: "/solutions", key: "solutions", label: t.nav.solutions },
    { to: "/partnerships", key: "partnerships", label: t.nav.partnerships },
    { to: "/about", key: "about", label: t.nav.about },
  ];

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-[#061A26]/85 backdrop-blur-xl border-b border-white/10 py-3"
            : "bg-transparent border-b border-transparent py-5"
        }`}
      >
        <div className="max-w-[1320px] mx-auto px-6 md:px-10 flex items-center justify-between gap-4">
          <Link to={path("/")} aria-label={t.a11y.homeLink} data-testid="header-logo-link" className="shrink-0">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8" aria-label={t.a11y.mainNav}>
            {links.map((l) => (
              <NavLink
                key={l.key}
                to={path(l.to)}
                data-testid={`nav-${l.key}-link`}
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-wide transition-colors duration-300 relative group whitespace-nowrap ${
                    isActive ? "text-[#19C7C1]" : "text-white/80 hover:text-white"
                  }`
                }
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 w-0 h-px bg-[#19C7C1] transition-[width] duration-300 group-hover:w-full" />
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4 shrink-0">
            <LanguageSelector />
            <Link
              to={path("/contact")}
              data-testid="header-contact-cta"
              className="btn-primary text-[13px] px-6 py-2.5 whitespace-nowrap"
            >
              {t.nav.contact}
            </Link>
          </div>

          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label={open ? t.a11y.closeMenu : t.a11y.openMenu}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-400 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="mobile-menu-overlay"
      >
        <div className="absolute inset-0 bg-[#061A26]/98 backdrop-blur-2xl" />
        <nav className="relative h-full flex flex-col justify-center px-8 sm:px-10 gap-5 overflow-y-auto py-24" aria-label={t.a11y.mobileNav}>
          {links.map((l, i) => (
            <NavLink
              key={l.key}
              to={path(l.to)}
              data-testid={`mobile-nav-${l.key}-link`}
              style={{ transitionDelay: open ? `${i * 60 + 100}ms` : "0ms" }}
              className={`text-2xl font-semibold tracking-tight transition-[opacity,transform] duration-500 ${
                open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              } text-white hover:text-[#19C7C1]`}
            >
              {l.label}
            </NavLink>
          ))}
          <Link to={path("/contact")} data-testid="mobile-contact-cta" className="btn-primary text-sm px-7 py-3.5 w-fit mt-3">
            {t.nav.contact}
          </Link>
          <div className="mt-6 max-w-sm">
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-white/50 mb-3">{t.footer.language}</p>
            <LanguageSelector variant="inline" />
          </div>
        </nav>
      </div>
    </>
  );
};
